﻿import {
	ensureErrorType,
	getEqualityComparer,
	isNullOrEmpty,
	isPromiseAlike,
	readNestedProperty,
	writeNestedProperty
} from "@2d-front-monorepo/core";
import {FormEvent} from "react";
import {InitialState} from "../helpers/useObjectStore";
import {MvvmValidation, MvvmValidationMethod, MvvmValidationResult, Paths} from "./MvvmControlProps";

const onChangeError: string = "onChangeError";
const submitError: string = "submitError";

type QueuedValidationEntry = {
	path: string,
}

function isRequiredValueMissing(value: any): boolean {
	if (value == null) {
		return true;
	}

	if (typeof value === "string") {
		return isNullOrEmpty(value);
	}

	if (Array.isArray(value)) {
		return value.length === 0;
	}

	if (typeof value === "object") {
		return Object.keys(value).length === 0;
	}

	return isNaN(value);
}

export type ErrorEntry = {
	error: string;
	controlId: string;
	value: any;
	type: string;
}

export type MvvmUpdater<T extends object> = (action: T) => void;

export interface IMvvmBaseContextState {
	getExceptions(): Error[];

	hasExceptions(): boolean;

	isInvalid(): boolean;

	isSubmitting(): boolean;

	isValidating(): boolean;

	triggerValidation(): void;
}

export interface IMvvmContextState<T extends object> extends IMvvmBaseContextState {
	clearErrors(): void;

	getError(path: Paths<T>): ErrorEntry | undefined;

	getValue<TValue>(path: Paths<T>): TValue | undefined;

	setValue(path: Paths<T>, value: any, updateVersion: boolean): void;

	get state(): T;

	update(updater: MvvmUpdater<T>, fireOnChange?: boolean): void;

	validationEnds(): Promise<void>;
}

export type MvvmContextValidationResult<T> = {
	error: string | undefined;
	path: Paths<T>;
	model?: object;
	type?: string;
}

export type MvvmContextValidationSync<T extends object> = (model: T) => MvvmContextValidationResult<T>[];
export type MvvmContextValidationAsync<T extends object> = (model: T) => Promise<MvvmContextValidationResult<T>[]>;
export type MvvmContextValidationMethod<T extends object> =
	MvvmContextValidationSync<T>
	| MvvmContextValidationAsync<T>;

type ValidatorEntry = {
	controlId: string;
	validation: MvvmValidation<any>;
}

export type MvvmContextInitialState<T extends object> = T | (() => T);

function resolveInitialState<T extends object>(initialState: InitialState<T>): T {
	return typeof initialState === "function" ? (initialState as () => T)() : initialState;
}

export class MvvmContextState<T extends object> implements IMvvmContextState<T> {
	private _disposed: boolean;
	private readonly _errors: Map<string, ErrorEntry>;
	private readonly _exceptions: Map<string, Error>;
	private _globalValidation: MvvmContextValidationMethod<T> | undefined;
	private readonly _innerContexts: Set<MvvmContextState<any>>;
	private _isSubmitting: boolean;
	private _isValidating: boolean;
	private _onChange: ((data: T) => Promise<void>) | ((data: T) => void) | undefined;
	private _parentContext: MvvmContextState<any> | undefined;
	private readonly _queuedValidations: QueuedValidationEntry[];
	private _runValidationOnChange: boolean;
	private _state: T;
	private readonly _updateVersion: (v: number) => void;
	private _validationEnds: Promise<void> | undefined;
	private readonly _validators: Map<string, ValidatorEntry>;
	private _version: number = 0;

	constructor(state: MvvmContextInitialState<T>, setVersion: (v: number) => void) {
		this._state = resolveInitialState(state);
		this._errors = new Map();
		this._validators = new Map();
		this._exceptions = new Map();
		this._isSubmitting = this._isValidating = false;
		this._globalValidation = undefined;
		this._queuedValidations = [];
		this._updateVersion = setVersion;
		this._disposed = false;
		this._runValidationOnChange = false;
		this._innerContexts = new Set();
		this._parentContext = undefined;
	}

	clearErrors = (): void => {
		this._errors.clear();
		this._exceptions.clear();
	};

	getError = (path: Paths<T>): ErrorEntry | undefined => this._errors.get(path);

	getValue = <TValue>(path: Paths<T>): TValue | undefined => readNestedProperty(this._state, path);

	setValue = (path: Paths<T>, value: any, updateVersion: boolean = true): void => {
		let equalityComparer: { (a: any, b: any): boolean };
		const oldValue = readNestedProperty(this._state, path);
		if (oldValue == null && value == null) {
			return;
		}

		if (oldValue != null) {
			equalityComparer = getEqualityComparer(oldValue);
		} else {
			equalityComparer = getEqualityComparer(value);
		}

		if (equalityComparer(oldValue, value)) {
			return;
		}

		writeNestedProperty(this._state, path, value);

		if (!updateVersion) {
			return;
		}

		this.enqueueValidation(path);
	};

	get state(): T {
		return this._state;
	}

	update = (updater: (action: T) => void, fireOnChange?: boolean): void => {
		updater(this._state);
		this._updateVersionOnchange(fireOnChange ?? true);
	};

	validationEnds = (): Promise<void> => this._validationEnds ?? Promise.resolve();

	dispose = (): void => {
		if (this._disposed) {
			throw new Error("Already disposed");
		}
		this._disposed = true;
		this.clearErrors();
		this._validators.clear();
		this._queuedValidations.length = 0;
		this._state = undefined!;
		this._onChange = undefined;
		this._validationEnds = undefined;
		this._globalValidation = undefined;

		if (this._parentContext != null) {
			this._parentContext.unregisterChildren(this);
			this._parentContext = undefined;
		}
	};

	enqueueValidation = <TModel extends object>(path: Paths<TModel>): void => {
		if (this._validators.size === 0) {
			this._updateVersionOnchange(true);
			return;
		}

		if (this._queuedValidations.length === 0) {
			queueMicrotask(this._runQueuedValidations.bind(this));
		}

		this._queuedValidations.push({path});
	};

	getExceptions = (): Error[] => Array.from(this._exceptions.values());

	hasExceptions = (): boolean => this._exceptions.size > 0 || this._innerContexts.values().some(x => x._exceptions.size > 0);

	isInvalid = (): boolean => this._errors.size > 0 || this._innerContexts.values().some(x => x.isInvalid);

	isSubmitting = (): boolean => this._isSubmitting || this._innerContexts.values().some(x => x._isSubmitting);

	isValidating = (): boolean => this._isValidating || this._innerContexts.values().some(x => x._isValidating);

	triggerValidation = (): void => {
		this._triggerValidation(true);
		this._innerContexts.values().every(context => context._triggerValidation(true));
	};

	handleSubmit = async (event: FormEvent<HTMLFormElement>, onSubmit: (data: T) => Promise<void>): Promise<void> => {
		event.preventDefault();

		try {
			this._isSubmitting = true;
			this.clearErrors();

			if (this._validators.size > 0 || this._globalValidation != null) {
				this._updateVersionOnly();
			}

			if (await this._validateInnerContexts(event)) {
				return;
			}

			this._triggerValidation(false);
			await this.validationEnds();

			if (this.isInvalid()) {
				return;
			}

			this._updateVersionOnly();
			await onSubmit(this._state);

		} catch (error) {
			this.setException(submitError, ensureErrorType(error));

		} finally {
			this._isSubmitting = false;
			this._updateVersionOnly();
		}
	};

	registerChildren(context: MvvmContextState<any>): void {
		context.setParent(this);
		this._innerContexts.add(context);
	}

	registerValidation(path: Paths<T>, controlId: string, validation: MvvmValidation<any> | undefined): void {
		this._unsetValidation(path);

		if (validation == null) {
			return;
		}

		let existing = this._validators.get(path);
		if (existing == null) {
			existing = {controlId, validation};
			this._validators.set(path, existing);
			return;
		}

		existing.controlId = controlId;
		existing.validation = validation;
	}

	setException(name: string, error: Error | undefined): void {
		const oldValue = this._exceptions.get(name);
		if (oldValue === error) {
			return;
		}

		if (error == null) {
			this._exceptions.delete(name);
		} else {
			this._exceptions.set(name, error);
		}

		this._updateVersionOnly();
	}

	setOnChange(onChange: ((data: T) => Promise<void>) | ((data: T) => void) | undefined, runValidationOnChange: boolean): void {
		this._onChange = onChange;
		this._runValidationOnChange = runValidationOnChange;
	}

	setParent(context: MvvmContextState<any>): void {
		this._parentContext = context;
	}

	setValidation(validation: MvvmContextValidationMethod<T> | undefined) {
		this._globalValidation = validation;
	}

	unregisterChildren(context: MvvmContextState<any>): void {
		this._innerContexts.delete(context);
	}

	unregisterValidation(path: Paths<T>) {
		this._unsetValidation(path);
	}

	private async _callOnChange(): Promise<void> {
		if (!this._onChange) {
			return;
		}

		try {
			const awaitable = this._onChange(this._state);
			if (isPromiseAlike(awaitable)) {
				await awaitable;
			}

			this.setException(onChangeError, undefined);

		} catch (e) {
			this.setException(onChangeError, ensureErrorType(e));
		}
	}

	private _clearInnerErrors(): void {
		if (this._errors.size === 0) return;

		for (const key of this._errors.keys()) {
			if (key.startsWith("$inner_")) {
				this._errors.delete(key);
			}
		}
	}

	private _getValidationQueue(): QueuedValidationEntry[] {
		const items: QueuedValidationEntry[] = [];
		for (const path of this._validators.keys()) {
			items.push({path});
		}

		return items;
	}

	private async _isValid<TValue>(validate: MvvmValidationMethod<TValue> | undefined, controlId: string, path: string, value: TValue | undefined, type?: string): Promise<boolean> {
		if (validate == null) {
			return true;
		}

		let validationResult: string | MvvmValidationResult | undefined;
		const validationMaybePromise = validate(value);
		if (isPromiseAlike<string | MvvmValidationResult | undefined>(validationMaybePromise)) {
			validationResult = await validationMaybePromise;
		} else {
			validationResult = validationMaybePromise;
		}

		return this._setError(validationResult, path, controlId, value, type);
	}

	private async _runGlobalValidation(): Promise<void> {
		const globalValidation = this._globalValidation;
		if (!globalValidation) {
			return;
		}

		let validationResult: MvvmContextValidationResult<T>[];
		const validationMaybePromise = globalValidation(this._state);
		if (isPromiseAlike(validationMaybePromise)) {
			validationResult = await validationMaybePromise;
		} else {
			validationResult = validationMaybePromise;
		}

		for (const result of validationResult) {
			this._setError(result.error, result.path, "global", readNestedProperty(result.model ?? this._state, result.path));
		}
	}

	private _runQueuedValidations(): void {
		if (this._runValidationOnChange && this._onChange != null) {
			this._queuedValidations.length = 0;
			this._runValidations(this._getValidationQueue(), true);
			return;
		}

		const localValidations = [...this._queuedValidations];
		this._queuedValidations.length = 0;

		const runValidations = new Set<MvvmValidation<any>>();

		const filtered = localValidations.filter(entry => {
			const {path} = entry;
			const validator = this._validators.get(path);
			if (validator == null) {
				//there are no validators for this path
				return false;
			}

			if (!(validator.validation.required != null || validator.validation.validate != null)) {
				//there is no validation to run (this should never happen, as there should be at least one validator, but anyways...)
				return false;
			}

			if (runValidations.has(validator.validation)) {
				//this validation has already been run, so we don't need to run it again
				return false;
			}

			runValidations.add(validator.validation);
			return true;

		});

		this._runValidations(filtered, true);
	}

	private async _runValidation(entry: QueuedValidationEntry): Promise<void> {
		const {path} = entry;
		const validator = this._validators.get(path)!;

		const controlId = validator.controlId;
		const value = readNestedProperty(this._state, path);
		const required = validator.validation.required;
		this._setError(undefined, path, controlId, value);

		if (typeof required === "string") {
			if (isRequiredValueMissing(value)) {
				this._setError(required, path, controlId, value, "required");
				return
			}

		} else if (!await this._isValid(required, controlId, path, value, "required")) {
			return;
		}

		await this._isValid(validator.validation.validate, controlId, path, value);
	}

	private _runValidations(entries: QueuedValidationEntry[], issueVersionChange: boolean): void {
		this._isValidating = true;
		this._validationEnds = new Promise(async resolve => {
			if (issueVersionChange) {
				await this._callOnChange();
			}

			const errorCount = this._errors.size;
			await Promise.all(entries.map(x => this._runValidation(x)));
			await this._runGlobalValidation();

			if (issueVersionChange || this._errors.size > 0 || errorCount > 0) {
				this._updateVersionOnly();
			}

			this._clearInnerErrors();

			resolve();
			this._validationEnds = undefined;
			this._isValidating = false;
		});
	}

	private _setError(error: string | MvvmValidationResult | undefined, path: string, controlId: string, value: any, type: string | undefined = undefined): boolean {
		if (error == null) {
			this._unsetError(path);
			return true;
		}

		let message: string;
		let errorType: string | undefined;

		if (typeof error === "string") {
			message = error;
			errorType = type;
		} else {
			message = error.error;
			errorType = type || error.type;
		}

		if (isNullOrEmpty(message)) {
			this._unsetError(path);
			return true;
		}

		this._errors.set(path, {error: message, controlId, value, type: errorType ?? "validation"});
		return false;
	}

	private _triggerValidation(issueVersionChange: boolean): void {
		this._runValidations(this._getValidationQueue(), issueVersionChange);
	}

	private _unsetError(path: string): void {
		this._errors.delete(path);
	}

	private _unsetValidation(path: string): void {
		this._validators.delete(path);
	}

	private _updateVersionOnchange(fireOnChange: boolean): void {
		this._updateVersionOnly();

		if (!fireOnChange) {
			return;
		}

		void this._callOnChange();
	}

	private _updateVersionOnly(): void {
		this._updateVersion(++this._version);
	}

	private async _validateInnerContexts(event: FormEvent<HTMLFormElement>): Promise<boolean> {
		if (this._innerContexts.size <= 0) {
			return false;
		}

		let isInvalid = false;

		await Promise.all(Array.from(this._innerContexts).map(async context => {
			await context.handleSubmit(event, () => Promise.resolve());
			isInvalid ||= context.isInvalid();

			if (context._exceptions.size > 0) {
				context._exceptions.entries().map(([key, error]) => this._exceptions.set(key, error));
			}

			if (context._errors.size > 0) {
				context._errors.entries().map(([key, error]) => this._errors.set(`$inner_${key}`, error));
			}
		}));

		if (isInvalid) {
			this._updateVersionOnly();
		}

		return isInvalid;
	}
}
